import { ReactNode } from 'react';
import { UseMutationOptions, UseMutationResult } from '@tanstack/react-query';
import { Hooks } from '@wellstone-solutions/common';
import { Form as MuiForm, Stack } from '@wellstone-solutions/web';
import { AxiosError, AxiosResponse } from 'axios';
import { AnyObjectSchema } from 'yup';

import { Button } from '@components/ui';
import { FORM_BUTTON_LABELS } from '@modules/auth/constants';

import type { ForgotPasswordSchemaType } from '@modules/auth/schemas/forgot-password-schema';
import type { ResetPasswordSchemaType } from '@modules/auth/schemas/reset-password-schema';
import type {
  PasswordResetConfirmInputRequest,
  PasswordResetRequestInputRequest,
} from '@wellstone-solutions/api/v2';

type FormikResetPasswordFormType = ReturnType<
  typeof Hooks.useForm<ForgotPasswordSchemaType | ResetPasswordSchemaType>
>;
type FormValues = ForgotPasswordSchemaType | ResetPasswordSchemaType;
type FormResponse =
  | PasswordResetRequestInputRequest
  | PasswordResetConfirmInputRequest;

type ResetPasswordFormType = <TData = FormResponse, TVariables = unknown>(
  props: FormProps<TData, TVariables>
) => React.ReactElement;

interface FormProps<TData = FormResponse, TVariables = unknown> {
  children: (form: FormikResetPasswordFormType) => ReactNode;
  formId: string;
  initialValues: FormValues;
  message?: string;
  mutateOptions: UseMutationOptions<
    AxiosResponse<TData>,
    AxiosError<{ [key: string]: string[] }>,
    TVariables,
    unknown
  >;
  mutation: UseMutationResult<
    AxiosResponse<TData>,
    AxiosError<{ [key: string]: string[] }>,
    TVariables
  >;
  schema: AnyObjectSchema;
}

const ResetPasswordForm: ResetPasswordFormType = ({
  children,
  formId,
  initialValues,
  message,
  mutateOptions,
  mutation,
  schema,
}) => {
  const { mutate, isPending, isSuccess } = mutation;

  const formik = Hooks.useForm({
    schema: schema,
    validationEngine: 'yup',
    initialValues: initialValues,
    onSubmit: (values) => {
      if (isPending || isSuccess) return;

      mutate(values, {
        onError: (error) => {
          if (!error?.response?.data) return;

          // Attempt to map field errors from the server to the form
          Object.entries(error.response.data).forEach(([key, values]) => {
            formik.setFieldError(key, values.join(' '));
          });
        },
        onSettled: () => {
          // Resets the form button state
          formik.setSubmitting(false);
        },
        ...mutateOptions,
      });
    },
  });

  return (
    <MuiForm onSubmit={formik.handleSubmit} name={formId} id={formId}>
      <Stack spacing={2} sx={{ alignItems: 'center' }}>
        {message && <p>{message}</p>}
        {children(formik)}
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={!(formik.isValid && formik.dirty) || formik.isSubmitting}>
          {FORM_BUTTON_LABELS[formId] ?? 'Save'}
        </Button>
      </Stack>
    </MuiForm>
  );
};

export { ResetPasswordForm };
